import {
	brokerContactCreationSubRoles,
	contactCreationRoles,
	duneContactCreationSubRoles,
	getErrorMessage,
	getStandardErrorData,
} from "@dune/vars";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";
import {
	createBrokerContact,
	createDuneContact,
	createInsurerContact,
	createSyndicContact,
	createTPAContact,
} from "../../api/mutations/contacts.mutations";
import { contactsQueryKeys } from "../../api/query-keys/contacts.query-keys";

export const zAddContactFormSchema = z
	.object({
		type: z.enum(contactCreationRoles).nullable(),
		email: z.email("Email invalide").min(1, "L'email est requis"),
		firstname: z.string().min(1, "Le prénom est requis"),
		lastname: z.string().min(1, "Le nom est requis"),
		giveAccessToPlatform: z.boolean().optional(),

		// BROKER
		brokerSocietyId: z.string(),
		brokerSubRole: z.enum(brokerContactCreationSubRoles).nullable(),

		// SYNDIC
		syndicSocietyId: z.string(),

		// DUNE
		duneSubRole: z.enum(duneContactCreationSubRoles).nullable(),

		// INSURER
		insurerSocietyId: z.string(),

		// TPA
		tpaSocietyId: z.string(),
	})
	.superRefine((data, ctx) => {
		if (!data.type) {
			ctx.addIssue({
				code: "custom",
				path: ["type"],
				message: "Veuillez sélectionner un type",
			});
		}
		if (data.giveAccessToPlatform === true) {
			if (!data.email) {
				ctx.addIssue({
					code: "custom",
					path: ["email"],
					message: "Veuillez renseigner un email",
				});
			}
		}
	});

export type ContactFormData = z.infer<typeof zAddContactFormSchema>;

export const emptyContactDefaultValues: ContactFormData = {
	type: null,
	email: "",
	firstname: "",
	lastname: "",
	giveAccessToPlatform: false,

	// BROKER
	brokerSocietyId: "",
	brokerSubRole: null,

	// SYNDIC
	syndicSocietyId: "",

	// DUNE
	duneSubRole: null,

	// INSURER
	insurerSocietyId: "",

	// TPA
	tpaSocietyId: "",
};

export const useAddContactForm = (onSuccess: () => void) => {
	const formMethods = useForm<ContactFormData>({
		defaultValues: emptyContactDefaultValues,
		resolver: zodResolver(zAddContactFormSchema),
	});

	const { handleSubmit, setError, reset } = formMethods;

	const queryClient = useQueryClient();

	const resetValues = () => {
		reset(emptyContactDefaultValues);
	};

	const onCreateSuccess = () => {
		queryClient.invalidateQueries({
			queryKey: contactsQueryKeys.lists(),
		});
		resetValues();
		onSuccess?.();
	};

	const onErrorCommon = (error: unknown) => {
		const errorData = getStandardErrorData(error);
		if (errorData?.statusCode === 409) {
			toast.error("Cette adresse email est déjà utilisée");
			return;
		}
		const errorMessage = getErrorMessage(error);
		toast.error(errorMessage);
	};

	const {
		mutate: createBrokerContactMutate,
		isPending: isCreateBrokerPending,
	} = useMutation({
		mutationFn: createBrokerContact,
		onSuccess: () => {
			onCreateSuccess();
		},
		onError: onErrorCommon,
	});

	const {
		mutate: createSyndicContactMutate,
		isPending: isCreateSyndicPending,
	} = useMutation({
		mutationFn: createSyndicContact,
		onSuccess: () => {
			onCreateSuccess();
		},
		onError: onErrorCommon,
	});

	const { mutate: createDuneContactMutate, isPending: isCreateDunePending } =
		useMutation({
			mutationFn: createDuneContact,
			onSuccess: () => {
				onCreateSuccess();
			},
			onError: onErrorCommon,
		});

	const {
		mutate: createInsurerContactMutate,
		isPending: isCreateInsurerPending,
	} = useMutation({
		mutationFn: createInsurerContact,
		onSuccess: () => {
			onCreateSuccess();
		},
		onError: onErrorCommon,
	});

	const { mutate: createTPAContactMutate, isPending: isCreateTPAPending } =
		useMutation({
			mutationFn: createTPAContact,
			onSuccess: () => {
				onCreateSuccess();
			},
			onError: onErrorCommon,
		});

	const onSubmitCreate = () =>
		handleSubmit((data) => {
			switch (data.type) {
				case "BROKER":
					if (!data.brokerSocietyId) {
						setError("brokerSocietyId", {
							type: "manual",
							message: "Veuillez sélectionner une société",
						});
						return;
					}
					if (!data.brokerSubRole) {
						setError("brokerSubRole", {
							type: "manual",
							message: "Veuillez sélectionner un rôle",
						});
						return;
					}
					createBrokerContactMutate({
						email: data.email,
						firstname: data.firstname,
						lastname: data.lastname,
						giveAccessToPlatform: data.giveAccessToPlatform,
						brokerSocietyId: Number(data.brokerSocietyId),
						brokerSubRole: data.brokerSubRole,
					});
					break;
				case "SYNDIC":
					if (!data.syndicSocietyId) {
						setError("syndicSocietyId", {
							type: "manual",
							message: "Veuillez sélectionner une société",
						});
						return;
					}
					createSyndicContactMutate({
						email: data.email,
						firstname: data.firstname,
						lastname: data.lastname,
						giveAccessToPlatform: data.giveAccessToPlatform,
						syndicSocietyId: Number(data.syndicSocietyId),
					});
					break;
				case "DUNE":
					if (!data.duneSubRole) {
						setError("duneSubRole", {
							type: "manual",
							message: "Veuillez sélectionner un rôle",
						});
						return;
					}
					createDuneContactMutate({
						email: data.email,
						firstname: data.firstname,
						lastname: data.lastname,
						giveAccessToPlatform: data.giveAccessToPlatform,
						duneSubRole: data.duneSubRole,
					});
					break;
				case "INSURER":
					if (!data.insurerSocietyId) {
						setError("insurerSocietyId", {
							type: "manual",
							message: "Veuillez sélectionner une société",
						});
						return;
					}
					createInsurerContactMutate({
						email: data.email,
						firstname: data.firstname,
						lastname: data.lastname,
						giveAccessToPlatform: data.giveAccessToPlatform,
						insurerSocietyId: Number(data.insurerSocietyId),
					});
					break;
				case "TPA":
					if (!data.tpaSocietyId) {
						setError("tpaSocietyId", {
							type: "manual",
							message: "Veuillez sélectionner une société",
						});
						return;
					}
					createTPAContactMutate({
						email: data.email,
						firstname: data.firstname,
						lastname: data.lastname,
						giveAccessToPlatform: data.giveAccessToPlatform,
						tpaSocietyId: Number(data.tpaSocietyId),
					});
					break;
				default:
					break;
			}
		});

	return {
		onSubmit: onSubmitCreate,
		isLoading:
			isCreateBrokerPending ||
			isCreateSyndicPending ||
			isCreateDunePending ||
			isCreateInsurerPending ||
			isCreateTPAPending,
		formMethods,
	};
};
